import BaseModal from '@/components/BaseModal';
import BoldText from '@/components/BoldText';
import { openCrisp } from '@/helpers';
import useToast from '@/hooks/useToast';
import { apiCaller } from '@/redux/query';
import languageRuleSlice, {
  backupSelectedLanguageRuleSelector,
  initLanguageRule,
  selectedLanguageRuleSelector,
} from '@/redux/slice/languageRule.slice';
import { IResponseApi } from '@/types/apis/response';
import { ILanguageRuleContent } from '@/types/languageRule.type';
import { Banner, Box, Button } from '@shopify/polaris';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModalContent from './components/ModalContent';
import { IParamsApi } from '@/types/apis/params';
import ModalConfirmLeavePage from '@/components/ModalConfirmLeavePage';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINT } from '@/constants/enum';

interface IProps {
  id?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setId: (id: number | undefined) => void;
}

const CUModal = ({ id, isOpen, setIsOpen, setId }: IProps) => {
  const { show } = useToast();
  const dispatch = useDispatch();
  const [createLanguageRule, { isLoading: isLoadingCreate }] = apiCaller.useCreateLanguageRuleMutation();
  const [updateLanguageRule, { isLoading: isLoadingUpdate }] = apiCaller.useUpdateLanguageRuleMutation();
  const [error, setError] = useState<string>('');
  const [openModalConfirmLeave, setOpenModalConfirmLeave] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });

  const [isShowModalDiscard, setIsShowModalDiscard] = useState(false);

  const currentLanguageRule = useSelector(selectedLanguageRuleSelector);
  const backupSelectedRule = useSelector(backupSelectedLanguageRuleSelector);
  const handleClose = () => {
    setOpenModalConfirmLeave(false);
    setTimeout(() => {
      setIsOpen(false);
      setId(undefined);
    }, 100);
  };

  const isDataNotChanged = useMemo(() => {
    return isEqual(currentLanguageRule, backupSelectedRule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentLanguageRule), JSON.stringify(backupSelectedRule)]);

  const handleDiscard = () => {
    dispatch(languageRuleSlice.actions.handleSetSelectedRule(backupSelectedRule));
    setIsShowModalDiscard(false);
  };

  const handleGetValidRuleContents = useCallback(() => {
    return currentLanguageRule.ruleContents?.filter((item) => {
      let isHasValue = Object.keys(item).some((key) => key !== 'id' && item[key as keyof ILanguageRuleContent]);
      return isHasValue;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentLanguageRule.ruleContents)]);
  const handleGetValidCheckoutRules = useCallback(() => {
    return currentLanguageRule.checkoutContent?.checkoutRules?.filter((item) => !!item.messageText) || [];
  }, [currentLanguageRule.checkoutContent?.checkoutRules]);

  const handleSave = async () => {
    const dataSubmit: IParamsApi.ICreateLanguageRule = {
      ...currentLanguageRule,
      ruleContents: handleGetValidRuleContents(),
      appearanceContent: currentLanguageRule.appearanceContent || initLanguageRule.appearanceContent,
      checkoutContent: {
        ...(currentLanguageRule.checkoutContent || initLanguageRule.checkoutContent),
        checkoutRules: handleGetValidCheckoutRules(),
      },
    };

    let res: IResponseApi.ICommon;
    try {
      if (id) {
        res = await updateLanguageRule({ ...dataSubmit, id }).unwrap();
      } else {
        res = await createLanguageRule(dataSubmit).unwrap();
      }
      if (res.status === 'success') {
        show(id ? 'Updated' : 'Saved');
        handleClose();
      } else {
        setError('Something wrong, please try again!');
      }
    } catch (error) {
      setError('Something wrong, please try again!');
    }
  };

  const isDisabledAction = useMemo(() => {
    const isNotSetAnyRuleContent = handleGetValidRuleContents()?.length === 0;
    const isNotSetAppearance =
      !currentLanguageRule.appearanceContent || Object.values(currentLanguageRule.appearanceContent).every((item) => item === '');
    const isNotSetCheckoutContent =
      !currentLanguageRule.checkoutContent ||
      Object.values(currentLanguageRule.checkoutContent).every((item) => {
        if (typeof item === 'string') return item === '';
        if (Array.isArray(item)) return item.length === 0 || item.every((rule) => rule.messageText === '');
        return false;
      });
    return isDataNotChanged || !!error || (isNotSetAnyRuleContent && isNotSetAppearance && isNotSetCheckoutContent);
  }, [
    handleGetValidRuleContents,
    currentLanguageRule.appearanceContent,
    currentLanguageRule.checkoutContent,
    isDataNotChanged,
    error,
  ]);

  useEffect(() => {
    if (error) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentLanguageRule)]);

  const handleBeforeClose = () => {
    if (!isDataNotChanged) {
      setOpenModalConfirmLeave(true);
    } else {
      handleClose();
    }
  };

  return (
    <BaseModal
      size="large"
      title={
        <div
          className={`d-flex w-full ${isMobile ? 'flex-column' : 'flex-row align-center content-between'}`}
          id="language-rule-modal-title"
        >
          <BoldText>Synctrack Estimated Delivery Date</BoldText>{' '}
          <div className={`d-flex gap-4 ${isMobile ? 'mt-8' : ''}`}>
            <Button
              onClick={() => setIsShowModalDiscard(true)}
              disabled={isDisabledAction}
              loading={isLoadingCreate || isLoadingUpdate}
            >
              Discard
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isDisabledAction}
              loading={isLoadingCreate || isLoadingUpdate}
            >
              Save
            </Button>
          </div>
        </div>
      }
      open={isOpen}
      onClose={handleBeforeClose}
    >
      {error && (
        <div className="mb-8">
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
            <div className="mt-8">
              <Button onClick={openCrisp}>Contact support</Button>
            </div>
          </Banner>
        </div>
      )}
      <ModalContent />
      <BaseModal
        title={'Discard all unsaved changes'}
        open={isShowModalDiscard}
        onClose={() => setIsShowModalDiscard(false)}
        primaryAction={{
          content: 'Discard changes',
          onAction: handleDiscard,
          id: 'btn-destructive-tone',
        }}
        secondaryActions={[
          {
            content: 'Continue editing',
            onAction: () => setIsShowModalDiscard(false),
          },
        ]}
        children={
          <div id="language-rule-modal-discard-container">
            <Box padding="200">If you discard changes, you’ll delete any edits you made since you last saved.</Box>
          </div>
        }
      />
      <ModalConfirmLeavePage
        open={openModalConfirmLeave}
        onClose={() => setOpenModalConfirmLeave(false)}
        onConfirm={handleClose}
        confirmButtonText="Confirm"
      />
    </BaseModal>
  );
};

export default CUModal;
